/**
 * 인하우스 Select(box 변형)의 클래스 문자열.
 *
 * 예전 react-select 의 boxStyle.ts / commonStyle.ts 를 그대로 옮긴 것이라 수치는 원본
 * 렌더 결과와 동일하다.
 *   control  : min-height 48px / padding 8px 16px / radius 12px / 1px solid
 *              coreProductBorder / 16px 400 140% / cursor pointer
 *   value    : coreGrayBlack, letter-spacing -0.32px (disabled 는 coreGray04)
 *   place    : coreProductPlaceholder + ellipsis
 *   indicator: padding 0, gap 8px
 *
 * tailwind.css 가 `@import 'tailwindcss' important` 라 모든 유틸리티가 !important 다.
 * 같은 속성을 두 번 emit 하면 className 순서가 아니라 CSS 출력 순서로 승자가 정해지므로
 * border-color / background-color / color 처럼 상태별로 갈리는 값은 여기서 하나만 고른다.
 */
import {
  dropDownItemClass,
  dropDownListClass,
  dropDownMenuClass,
} from '../../../dropdowns/dropDownClasses';
import type { SelectAppearance } from '../types/SelectAppearance';

type ControlState = {
  hasError: boolean;
  isDisabled: boolean;
  isOpen: boolean;
  isReadOnly: boolean;
};

/** 메뉴 높이 추정치(자동 배치 판단용). menuList max-height 280px + 메뉴 padding 8*2 + 4px 간격 */
const MENU_MAX_LIST_HEIGHT = 280;
const MENU_CHROME_HEIGHT = 20;

/**
 * reset.css 가 button 의 outline 을 none 으로 지우기 때문에 포커스 링을 직접 준다.
 * (utilities 레이어가 base 레이어를 항상 이긴다)
 */
const CONTROL_BASE =
  'flex min-h-12 w-full cursor-pointer items-center gap-2 rounded-xl border border-solid px-4 py-2 text-left text-base font-normal leading-[140%] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-apps-corePrimary01';

export function selectControlClass({
  hasError,
  isDisabled,
  isOpen,
  isReadOnly,
}: ControlState) {
  /**
   * 원본은 boxStyle 의 borderColor(menuIsOpen ? primary : border) 위에
   * Select.tsx 가 hasError 를 무조건 덮어썼다. 그 우선순위를 그대로 유지한다.
   */
  const border = hasError
    ? 'border-apps-coreProductError01'
    : isOpen
      ? 'border-apps-corePrimary01'
      : 'border-apps-coreProductBorder';

  /* 원본의 isReadOnly || isDisabled 처리: 배경 교체 + pointer-events: none */
  const surface =
    isDisabled || isReadOnly
      ? 'pointer-events-none bg-apps-coreProductDisableContainer'
      : 'bg-apps-coreGrayWhite';

  return `${CONTROL_BASE} ${border} ${surface}`;
}

export function selectValueClass({
  hasValue,
  isDisabled,
}: {
  hasValue: boolean;
  isDisabled: boolean;
}) {
  const color = !hasValue
    ? 'text-apps-coreProductPlaceholder'
    : isDisabled
      ? 'text-apps-coreGray04'
      : 'text-apps-coreGrayBlack';

  /* letter-spacing 은 원본에서 singleValue 에만 있었고 placeholder 에는 없었다 */
  const tracking = hasValue ? 'tracking-[-0.32px]' : '';

  return `min-w-0 flex-1 truncate ${color} ${tracking}`;
}

/**
 * indicatorsContainer: padding 0 + gap 8px.
 *
 * 색은 반드시 여기서 지정해야 한다. 셰브론 SVG 가 stroke="currentColor" 라 색을 주지
 * 않으면 button 이 상속받은 본문 색(#000)을 그대로 쓰게 되는데, react-select 의
 * dropdownIndicator 기본값은 `isFocused ? neutral60 : neutral20`
 * (= hsl(0,0%,40%) / hsl(0,0%,80%)) 이었고 BoxStyle/CommonSelectStyle 어느 쪽도 이를
 * 덮어쓰지 않았다. 즉 원본 셰브론은 평상시 #cccccc(=컨트롤 보더와 같은 톤),
 * 포커스/열림 상태에서 #666666 이었다.
 * 팔레트에서 가장 가까운 토큰인 coreProductBorder(#cccfd5) / coreGray02(#7a7b88) 로 맞춘다.
 * color 는 상태별로 갈리므로 유틸리티를 겹치지 않고 JS 에서 하나만 고른다.
 */
export function selectIndicatorsClass({ isOpen }: { isOpen: boolean }) {
  const color = isOpen ? 'text-apps-coreGray02' : 'text-apps-coreProductBorder';

  return `pointer-events-none flex shrink-0 items-center gap-2 p-0 ${color}`;
}

/**
 * 메뉴를 감싸는 절대 위치 래퍼.
 * z-index 는 portalZIndex 가 자유 수치라 style 속성으로 넘긴다. 유틸리티(z-*)를 같이
 * 붙이면 !important 라 inline style 을 이겨버리므로 여기서는 z 유틸리티를 쓰지 않는다.
 */
export function selectMenuPositionClass({
  isSmallSize,
  placement,
  rightAligned,
}: {
  isSmallSize: boolean;
  placement: 'bottom' | 'top';
  rightAligned: boolean;
}) {
  return [
    'absolute',
    placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1',
    rightAligned ? 'right-0' : 'left-0',
    isSmallSize ? '' : 'w-full',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * 공개 사이트(apps) 룩. Select.tsx 의 기본값이다.
 * 어드민은 같은 Select.tsx 에 자기 appearance 를 넣어 재사용한다.
 */
export const appsSelectAppearance: SelectAppearance = {
  container: () => 'relative',
  control: ({ hasError, isDisabled, isOpen, isReadOnly }) =>
    selectControlClass({ hasError, isDisabled, isOpen, isReadOnly }),
  value: ({ hasValue, isDisabled }) =>
    selectValueClass({ hasValue, isDisabled }),
  indicators: ({ isOpen }) => selectIndicatorsClass({ isOpen }),
  indicatorIcon: ({ isInteractive }) =>
    isInteractive ? undefined : 'opacity-40',
  indicatorIconSize: 16,
  menuWrapper: ({ isSmallSize, placement, rightAligned }) =>
    selectMenuPositionClass({ isSmallSize, placement, rightAligned }),
  menu: ({ isSmallSize }) => dropDownMenuClass(isSmallSize),
  menuStyle: ({ isSmallSize }) => (isSmallSize ? undefined : { width: '100%' }),
  list: dropDownListClass,
  item: (isSmallSize, item) => dropDownItemClass(isSmallSize, item),
  estimateMenuHeight: (optionCount, isSmallSize) =>
    Math.min(optionCount * (isSmallSize ? 40 : 48), MENU_MAX_LIST_HEIGHT) +
    MENU_CHROME_HEIGHT,
};
